import { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CANTIERE_STATUS, DOC_CATEGORIES, PHASE_STATUS, PROVENIENZE } from "@/lib/format";
import { Btn, Chip, Field, Modal } from "@/components/rp";
import { AddressInput } from "@/components/AddressInput";
import { ClientLogiFields } from "@/components/commesse";

export const useSubmit = (fn, done) => {
  const [loading, setLoading] = useState(false);
  const run = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      done(await fn());
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return [loading, run];
};

export const Actions = ({ onCancel, loading, label, testid }) => (
  <div className="flex justify-end gap-2 pt-2">
    <Btn type="button" variant="secondary" onClick={onCancel}>Annulla</Btn>
    <Btn type="submit" loading={loading} data-testid={testid}>{label}</Btn>
  </div>
);

export const PhaseDialog = ({ cantiere, phase, onClose, onSaved }) => {
  const [status, setStatus] = useState(phase.status);
  const [note, setNote] = useState(phase.note || "");
  const [task, setTask] = useState("");
  const base = `/cantieri/${cantiere.id}`;
  const [loading, save] = useSubmit(async () => (await api.patch(`${base}/phases/${phase.key}`, { status, note })).data, (c) => { onSaved(c); toast.success("Fase aggiornata"); onClose(); });
  const addTask = async () => {
    if (!task.trim()) return;
    try { onSaved((await api.post(`${base}/phases/${phase.key}/tasks`, { title: task })).data); setTask(""); } catch (e) { toast.error(errMsg(e)); }
  };
  const delTask = async (t) => {
    try { onSaved((await api.delete(`${base}/tasks/${t.id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const current = cantiere.phases.find((p) => p.key === phase.key) || phase;
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Gestisci fase · ${phase.name}`} testid="phase-dialog">
      <form onSubmit={save} className="space-y-5">
        <div className="space-y-3"><p className="rp-label">Stato</p>
          <div className="flex flex-wrap gap-2">{Object.entries(PHASE_STATUS).map(([v, l]) => <Chip key={v} active={status === v} onClick={() => setStatus(v)} data-testid={`phase-status-chip-${v}`}>{l}</Chip>)}</div>
          {status === "done" && <p className="text-xs text-[#757575]">Tutte le attività verranno segnate come fatte.</p>}
        </div>
        <Field label="Nota per il cliente"><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rp-field" data-testid="phase-note-input" /></Field>
        <div className="space-y-2"><p className="rp-label">Attività</p>
          {current.tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 bg-[#F7F7F7] rounded-xl px-3 py-2 text-sm">
              <span className={t.done ? "" : "text-[#757575]"}>{t.title}</span>
              <button type="button" onClick={() => delTask(t)} className="text-[#757575] hover:text-[#DC3545]" data-testid={`phase-task-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <input value={task} onChange={(e) => setTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTask())} className="rp-field h-11" placeholder="Nuova attività" data-testid="phase-new-task-input" />
            <Btn type="button" variant="secondary" onClick={addTask} data-testid="phase-add-task-button"><Plus className="h-4 w-4" /></Btn>
          </div>
        </div>
        <Actions onCancel={onClose} loading={loading} label="Salva fase" testid="phase-save-button" />
      </form>
    </Modal>
  );
};

export const CantiereFormDialog = ({ open, onClose, cantiere, clientId, onSaved }) => {
  const empty = { title: "", address: "", description: "", start_date: "", expected_end: "", status: "attivo" };
  const [f, setF] = useState(empty);
  useEffect(() => {
    if (!open) return;
    setF(cantiere ? { ...empty, ...cantiere } : empty);
    if (!cantiere && clientId) api.get(`/admin/clients/${clientId}`).then((r) => setF((x) => ({ ...x, address: x.address || r.data.address || "" }))).catch(() => {});
  }, [open]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [loading, save] = useSubmit(async () => {
    const body = { title: f.title, address: f.address, description: f.description, start_date: f.start_date, expected_end: f.expected_end };
    return cantiere ? (await api.patch(`/cantieri/${cantiere.id}`, { ...body, status: f.status })).data : (await api.post("/cantieri", { ...body, client_id: clientId })).data;
  }, (c) => { toast.success(cantiere ? "Cantiere aggiornato" : "Cantiere creato"); onSaved(c); onClose(); });
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title={cantiere ? "Modifica cantiere" : "Nuovo cantiere"} testid="cantiere-form-dialog" wide>
      <form onSubmit={save} className="space-y-4">
        <Field label="Titolo"><input required value={f.title} onChange={set("title")} className="rp-field" data-testid="cantiere-title-input" /></Field>
        <Field label="Indirizzo" hint={!cantiere && clientId ? "Precompilato con l'indirizzo del cliente: cambialo solo se il cantiere è altrove." : undefined}><AddressInput value={f.address} onChange={(v) => setF({ ...f, address: v })} testid="cantiere-address" /></Field>
        <Field label="Descrizione"><textarea rows={3} value={f.description} onChange={set("description")} className="rp-field" data-testid="cantiere-description-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Inizio"><input type="date" value={f.start_date || ""} onChange={set("start_date")} className="rp-field" data-testid="cantiere-start-input" /></Field>
          <Field label="Fine prevista"><input type="date" value={f.expected_end || ""} onChange={set("expected_end")} className="rp-field" data-testid="cantiere-end-input" /></Field>
        </div>
        {cantiere && (
          <div className="space-y-3"><p className="rp-label">Stato</p>
            <div className="flex flex-wrap gap-2">{Object.entries(CANTIERE_STATUS).map(([v, l]) => <Chip key={v} active={f.status === v} onClick={() => setF({ ...f, status: v })} data-testid={`cantiere-status-chip-${v}`}>{l}</Chip>)}</div>
          </div>
        )}
        <Actions onCancel={onClose} loading={loading} label={cantiere ? "Salva" : "Crea cantiere"} testid="cantiere-save-button" />
      </form>
    </Modal>
  );
};

export const UploadPhotoDialog = ({ open, onClose, cantiere, onSaved }) => {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [phaseKey, setPhaseKey] = useState("");
  useEffect(() => { if (open) { setFile(null); setCaption(""); setPhaseKey(cantiere.phases.find((p) => p.status === "active")?.key || ""); } }, [open]); // eslint-disable-line
  const [loading, save] = useSubmit(async () => {
    if (!file) throw { response: { data: { detail: "Seleziona una foto" } } };
    const fd = new FormData();
    fd.append("file", file); fd.append("caption", caption); fd.append("phase_key", phaseKey);
    return (await api.post(`/cantieri/${cantiere.id}/photos`, fd)).data;
  }, (p) => { toast.success("Foto caricata"); onSaved(p); onClose(); });
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title="Carica foto" testid="upload-photo-dialog">
      <form onSubmit={save} className="space-y-4">
        <Field label="Foto" hint="JPG, PNG, WEBP o HEIC · max 20MB"><input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(e) => setFile(e.target.files[0])} className="rp-field pt-3" data-testid="photo-file-input" /></Field>
        <Field label="Didascalia"><input value={caption} onChange={(e) => setCaption(e.target.value)} className="rp-field" data-testid="photo-caption-input" /></Field>
        <div className="space-y-3"><p className="rp-label">Fase</p>
          <div className="flex flex-wrap gap-2">{cantiere.phases.map((p) => <Chip key={p.key} active={phaseKey === p.key} onClick={() => setPhaseKey(p.key)} data-testid={`photo-phase-chip-${p.key}`}>{p.name}</Chip>)}</div>
        </div>
        <Actions onCancel={onClose} loading={loading} label="Carica" testid="photo-upload-submit" />
      </form>
    </Modal>
  );
};

export const UploadDocDialog = ({ open, onClose, cantiere, onSaved }) => {
  const [f, setF] = useState({ title: "", category: "preventivo", amount: "" });
  const [file, setFile] = useState(null);
  useEffect(() => { if (open) { setFile(null); setF({ title: "", category: "preventivo", amount: "" }); } }, [open]);
  const [loading, save] = useSubmit(async () => {
    if (!file) throw { response: { data: { detail: "Seleziona un file" } } };
    const fd = new FormData();
    fd.append("file", file); fd.append("title", f.title || file.name); fd.append("category", f.category);
    if (f.amount) fd.append("amount", f.amount);
    return (await api.post(`/cantieri/${cantiere.id}/documents`, fd)).data;
  }, (d) => { toast.success("Documento caricato"); onSaved(d); onClose(); });
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title="Carica documento" testid="upload-doc-dialog">
      <form onSubmit={save} className="space-y-4">
        <Field label="File" hint="PDF o immagine · max 20MB"><input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files[0])} className="rp-field pt-3" data-testid="doc-file-input" /></Field>
        <Field label="Titolo"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rp-field" data-testid="doc-title-input" /></Field>
        <div className="space-y-3"><p className="rp-label">Categoria</p>
          <div className="flex flex-wrap gap-2">{DOC_CATEGORIES.map((c) => <Chip key={c.v} active={f.category === c.v} onClick={() => setF({ ...f, category: c.v })} data-testid={`doc-category-chip-${c.v}`}>{c.l}</Chip>)}</div>
        </div>
        <Field label="Importo (€)" hint={f.category === "preventivo" ? "Il cliente potrà accettarlo o rifiutarlo" : "Facoltativo"}><input inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="rp-field" placeholder="es. 12500" data-testid="doc-amount-input" /></Field>
        <Actions onCancel={onClose} loading={loading} label="Carica" testid="doc-upload-submit" />
      </form>
    </Modal>
  );
};

export const NewClientDialog = ({ open, onClose, onSaved }) => {
  const empty = () => ({ name: "", email: "", phone: "", address: "", provenienza: "", provenienza_note: "", logistica: {} });
  const [f, setF] = useState(empty);
  useEffect(() => { if (open) setF(empty()); }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [loading, save] = useSubmit(async () => (await api.post("/admin/clients", f)).data, (r) => { toast.success(`Cliente creato: abbiamo inviato a ${f.email} l'email per scegliere la password`, { duration: 10000 }); onSaved(r); onClose(); });
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title="Nuovo cliente" description="Il cliente riceverà un'email con il link (valido 7 giorni) per scegliere da solo la sua password." testid="new-client-dialog" wide>
      <form onSubmit={save} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome e cognome"><input required value={f.name} onChange={set("name")} className="rp-field" data-testid="new-client-name-input" /></Field>
          <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className="rp-field" data-testid="new-client-email-input" /></Field>
          <Field label="Telefono"><input value={f.phone} onChange={set("phone")} className="rp-field" data-testid="new-client-phone-input" /></Field>
          <Field label="Provenienza">
            <select value={f.provenienza} onChange={set("provenienza")} className="rp-field" data-testid="new-client-provenienza-select">
              <option value="">Non indicata</option>
              {PROVENIENZE.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
        </div>
        {f.provenienza && <Field label="Dettaglio provenienza (facoltativo)"><input value={f.provenienza_note} onChange={set("provenienza_note")} className="rp-field" placeholder="Es. consigliato da Mario Rossi, post Instagram bagno" data-testid="new-client-provenienza-note-input" /></Field>}
        <Field label="Indirizzo dell'abitazione" hint="Viene usato in automatico per preventivi e cantieri: non dovrai riscriverlo."><AddressInput value={f.address} onChange={(v) => setF({ ...f, address: v })} testid="new-client-address" /></Field>
        <ClientLogiFields l={f.logistica} onChange={(v) => setF({ ...f, logistica: v })} />
        <p className="text-xs text-[#757575]">Il cantiere non viene creato ora: crea il preventivo dalla scheda cliente, diventerà cantiere quando il cliente lo accetta o con «Trasforma in cantiere».</p>
        <Actions onCancel={onClose} loading={loading} label="Crea e invia invito" testid="new-client-submit" />
      </form>
    </Modal>
  );
};

